import React, {
  useEffect, useState, useRef, ImgHTMLAttributes,
} from 'react';

// Global cache to store image loading promises and prevent re-fetching
const imageCache = new Map<string, Promise<string>>();
const loadedImages = new Map<string, HTMLImageElement>();
const imageLoadStatus = new Map<string, 'loading' | 'loaded' | 'error'>();

/**
 * Preloads an image and caches it to prevent Chrome from re-requesting
 * when img elements are unmounted/remounted during virtualization
 */
const preloadImage = (src: string): Promise<string> => {
  // Return cached promise if already loading/loaded
  if (imageCache.has(src)) {
    return imageCache.get(src)!;
  }

  const promise = new Promise<string>((resolve, reject) => {
    // Check if already loaded
    if (loadedImages.has(src)) {
      resolve(src);
      return;
    }

    imageLoadStatus.set(src, 'loading');
    const img = new Image();

    img.onload = () => {
      // Store the image element to keep it in memory
      loadedImages.set(src, img);
      imageLoadStatus.set(src, 'loaded');
      resolve(src);
    };

    img.onerror = () => {
      imageCache.delete(src);
      imageLoadStatus.set(src, 'error');
      reject(new Error(`Failed to load image: ${src}`));
    };

    img.src = src;
  });

  imageCache.set(src, promise);
  return promise;
};

interface CachedImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
}

/**
 * CachedImage component that prevents Chrome from re-requesting images
 * when the component is unmounted and remounted (e.g., during virtualized scrolling).
 *
 * This component preloads images and keeps them in memory, ensuring that
 * even if the img DOM node is destroyed and recreated, the browser can
 * reuse the cached resource without making new network requests.
 */
const CachedImage = React.memo<CachedImageProps>(({ src, alt, ...props }) => {
  const [isReady, setIsReady] = useState(() => imageLoadStatus.get(src) === 'loaded');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    if (!src) return undefined;

    // If already loaded, just render immediately
    if (imageLoadStatus.get(src) === 'loaded') {
      if (!isReady) setIsReady(true);
      return undefined;
    }

    // Otherwise, preload the image
    preloadImage(src)
      .then(() => {
        if (mountedRef.current) {
          setIsReady(true);
        }
      })
      .catch(() => {
        if (mountedRef.current) {
          setIsReady(true); // Still render even on error
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, [src]); // Removed isReady from deps to prevent re-running effect

  // Always render the img tag with the src to leverage browser caching
  // The opacity transition provides a smooth loading experience
  return (
    <img
      {...props}
      src={src}
      alt={alt}
      style={{
        ...props.style,
        opacity: isReady ? 1 : 0,
        transition: 'opacity 0.15s ease-out',
      }}
    />
  );
}, (prevProps, nextProps) => (
  // Only re-render if src changes
  prevProps.src === nextProps.src && prevProps.alt === nextProps.alt
));

CachedImage.displayName = 'CachedImage';

export default CachedImage;
